import classNames from 'classnames'
import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import useMeasure from 'react-use-measure'
import { createSideMenu, menuItems } from '../../constants/menuItems'
import { useIsMobile } from '../../hooks/useIsMobile'
import Breadcrumbs from './Breadcrumbs'
import Header from './Header'
import Menu from './Menu'
import MobileMenuAccessable from './MobileMenuAccessable'

import { useGetAllCards } from '../../hooks/apolloHooks/useGetAllCards'
import { useGetCustomer } from '../../hooks/apolloHooks/useGetCustomer'
import { useMessageBanner } from '../../hooks/useMessageBanner'
import { GET_BANKCARDS_CUSTOMER_DATA_QUERY, GET_BASIC_CUSTOMER_DATA_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries'
import { BankCardsCustomerDataResponse, BasicCustomerDataResponse } from '../../integrations/allkort/graphql/interface/Customer'
import ErrorDisplay from './ErrorDisplay'
import SimpleFooter from './SimpleFooter'

type Props = {
  children: React.ReactNode
}

const Page: React.FunctionComponent<Props> = ({ children }) => {
  const [headerRef, { height: headerHeight }] = useMeasure()
  const [bannersRef, { height: bannersHeight }] = useMeasure()

  // IMPORTANT: All hooks must be called BEFORE any conditional returns!
  const isMobile = useIsMobile()
  const location = useLocation()

  // Message banner eg. show status or frozen 
  const messageBanner = useMessageBanner()

  // Prettify name, sometimes it is capitalized
  const prettyfyName = (name: string) => {
    if (!name) {
      return 'Profile name'
    }
    const newName = name.split(' ').map((singleName) => (
      singleName = singleName.charAt(0).toUpperCase() + singleName.slice(1).toLowerCase()
    )).join(' ')
    return newName;
  }

  // Create stable variable objects to prevent infinite re-renders
  const bankCardsVariables = React.useMemo(() => ({ first: 1, after: null, before: null, last: null }), []);

  // Fetches basic customer data
  const { data: customerBasicData, loading: customerLoading, error: customerError } = useGetCustomer<BasicCustomerDataResponse>(
    GET_BASIC_CUSTOMER_DATA_QUERY);

  // Fetch all regular cards with automatic pagination
  const { cards: allCards, loading: cardsLoading, error: cardsError } = useGetAllCards();


  const { data: bankCardsData, loading: bankCardsLoading, error: bankCardsError } = useGetCustomer<BankCardsCustomerDataResponse>(
    GET_BANKCARDS_CUSTOMER_DATA_QUERY,
    bankCardsVariables,
    { fetchPolicy: 'network-only' }
  );

  // Animation for customer data loading
  if (customerLoading) {
    return (
      <div className="loading-page">
        <span className="loader-zoom">
        </span>
      </div>
    )
  }
  if (customerError) return (
    <ErrorDisplay errorContext="Fel vid laddning av kunddata:" errorMessage={customerError?.message} />
  );

  // Animation for cards loading
  if (cardsLoading) return (
    <div className="loading-page">
      <span className="loader-zoom">
      </span>
    </div>
  )
  if (cardsError) return (
    <ErrorDisplay errorContext="Fel vid laddning av kort(page):" errorMessage={cardsError?.message} />
  );

  // Animation for bank cards loading
  if (bankCardsLoading) return (
    <div className="loading-page">
      <span className="loader-zoom">
      </span>
    </div>
  )
  if (bankCardsError) return (
    <ErrorDisplay errorContext="Fel vid laddning av bankkort:" errorMessage={bankCardsError?.message} />
  );

  // Get cards and bankcards arrays
  const bankCards = (bankCardsData as any)?.currentCustomer?.bankCards?.nodes || [];

  // Filter cards by type (K = smartcards, P = creditcards) no need to check active cards here, 
  // menu should be shown if customer has any card of that type even if they are disabled
  const hasSmartCards = allCards.some(card => card.type?.shortName === 'P');
  const hasCreditCards = allCards.some(card => card.type?.shortName === 'K');
  const hasAtLeastOneBankCard = bankCards.length > 0;

  // Create menu items based on card presence
  const headerItems = menuItems(hasSmartCards, hasCreditCards, hasAtLeastOneBankCard)
  const sideItems = createSideMenu({
    isCompany: customerBasicData!.currentCustomer!.isCompany,
    name: prettyfyName(customerBasicData!.currentCustomer!.name),
  })

  /**
   * Returns active tab for mobile accessable menu
   */
  const getActiveTab = (pathname: string): number => {
    const pathTabMapping: { [key: string]: number } = {
      '/mitt-konto/dashboard': 1,
      '/fakturor-kontobesked': 2,
      '/kort': 3,
      '/mitt-konto/installningar': 4,
      '/mitt-konto/hantera-anvandare': 4,
      '/loggaut': 4
    };

    return pathTabMapping[pathname] || 0
  }

  // The main header gets shifted if a banner notice is shown. Adds a class to adjust height on main content
  let bannerClass = '';
  if ('true' === sessionStorage.getItem('banner')) {
    bannerClass = 'has-banner';
  }

  // Redirect from app links — must come after all hooks
  if (location.pathname === '/mitt-konto/' || location.pathname === '/mitt-konto') {
    const target = location.hash === '#remove-account'
      ? '/mitt-konto/installningar#remove-account'
      : '/mitt-konto/installningar';
    return <Navigate to={target} replace />;
  }

  return (
    <>
      <Header ref={headerRef}>
        {/* Shows messages in the top of the page eg, forzen, has status etc. */}
        {messageBanner}

        <>
          <Menu
            headerItems={headerItems}
            isCompany={customerBasicData?.currentCustomer?.isCompany ?? false}
            sideItems={sideItems}
            hasSmartCards={hasSmartCards}
            hasCreditCards={hasCreditCards}
          />
        </>
      </Header>

      <div
        className="MenuPusher"
        style={{
          /**
           * Push page down on mobile so that the banners don't cover important content
           */
          height: isMobile ? headerHeight : headerHeight - bannersHeight
        }}
      />
      <div className={
        // Adds page class to easier identify what is in the current view.
        classNames('PageContent-mypages', location.pathname?.slice(1).replace('/', '-'), bannerClass)
      }
        id="main">
        {
          // Breadcrumbs comonent
          <Breadcrumbs />
        }
        {children}
      </div>

      <SimpleFooter />
      <MobileMenuAccessable activeTab={getActiveTab(location.pathname)} />
    </>
  )
}

export default Page
